
interface RevenueStatsProps {
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  orderCount: number;
}

export function RevenueStats({ totalRevenue, totalExpenses, netProfit, orderCount }: RevenueStatsProps) {
  const formatCurrency = (amount: number) => {
    if (amount >= 1000000000) {
      return (amount / 1000000000).toFixed(1) + ' tỷ ₫';
    } else if (amount >= 1000000) {
      return (amount / 1000000).toFixed(0) + ' triệu ₫';
    }
    return amount.toLocaleString('vi-VN') + ' ₫';
  };

  const getChangePercentage = (current: number, previous: number) => {
    if (previous === 0) return '+0%';
    const change = ((current - previous) / previous) * 100;
    return change >= 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
  };

  // Mock previous period data for comparison
  const previousRevenue = totalRevenue * 0.85;
  const previousExpenses = totalExpenses * 0.92;
  const previousProfit = netProfit * 0.78;
  const previousOrders = Math.floor(orderCount * 0.82);

  const stats = [
    {
      title: 'Tổng doanh thu',
      value: totalRevenue,
      previous: previousRevenue,
      icon: 'ri-line-chart-line',
      gradient: 'from-green-500 to-emerald-500',
      bgGradient: 'from-green-50 to-emerald-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-700',
      valueColor: 'text-green-900'
    },
    {
      title: 'Tổng chi phí',
      value: totalExpenses,
      previous: previousExpenses,
      icon: 'ri-arrow-down-line',
      gradient: 'from-red-500 to-rose-500',
      bgGradient: 'from-red-50 to-rose-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-700',
      valueColor: 'text-red-900'
    },
    {
      title: 'Lợi nhuận ròng',
      value: netProfit,
      previous: previousProfit,
      icon: 'ri-money-dollar-circle-line',
      gradient: 'from-blue-500 to-indigo-500',
      bgGradient: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700',
      valueColor: 'text-blue-900'
    },
    {
      title: 'Tổng đơn hàng',
      value: orderCount,
      previous: previousOrders,
      icon: 'ri-shopping-cart-line',
      gradient: 'from-purple-500 to-violet-500',
      bgGradient: 'from-purple-50 to-violet-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-700',
      valueColor: 'text-purple-900',
      isCount: true
    }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
      {stats.map((stat, index) => (
        <div
          key={index}
          className={`bg-gradient-to-br ${stat.bgGradient} rounded-xl p-4 sm:p-6 border ${stat.borderColor} shadow-sm hover:shadow-md transition-all duration-200`}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${stat.textColor} truncate`}>
                {stat.title}
              </p>
            </div>
            <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r ${stat.gradient} rounded-full flex items-center justify-center flex-shrink-0 ml-2 shadow-sm`}>
              <i className={`${stat.icon} text-white text-lg sm:text-xl`}></i>
            </div>
          </div>

          {/* Value */}
          <div className="mb-3 sm:mb-4">
            <p className={`text-lg sm:text-2xl lg:text-3xl font-bold ${stat.valueColor} leading-tight`}>
              {stat.isCount ? stat.value.toLocaleString('vi-VN') : formatCurrency(stat.value)}
            </p>
          </div>

          {/* Change indicator */}
          <div className="flex items-center">
            <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              getChangePercentage(stat.value, stat.previous).startsWith('+') 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              <i className={`${
                getChangePercentage(stat.value, stat.previous).startsWith('+') 
                  ? 'ri-arrow-up-line' 
                  : 'ri-arrow-down-line'
              } mr-1`}></i>
              <span>{getChangePercentage(stat.value, stat.previous)}</span>
            </div>
            <span className={`${stat.textColor} text-xs ml-2 truncate`}>
              so với kỳ trước
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
